import api from '@shared/services/api';
import { MediaService } from '@shared/services/media.service';
import { MediaType } from '@shared/types/media.types';
import { TutorialBankItem } from '../types/tutorial-bank.types';

export class TutorialBankEditorService {
  /**
   * Get a tutorial bank item for editing
   * @param itemId ID of the item to edit
   * @returns The tutorial bank item with all related data
   */
  static async getTutorialBankItemForEditing(
    itemId: string
  ): Promise<TutorialBankItem> {
    const response = await api.get(`/tutorial-bank/items/${itemId}`);
    return response.data.item;
  }

  /**
   * Save a tutorial bank item
   * @param itemId ID of the item to save
   * @param itemData Updated item data
   * @returns The saved tutorial bank item
   */
  static async saveTutorialBankItem(
    itemId: string,
    itemData: Partial<TutorialBankItem>
  ): Promise<TutorialBankItem> {
    const response = await api.put(`/tutorial-bank/items/${itemId}`, itemData);
    return response.data.item;
  }

  /**
   * Create a new tutorial bank item
   * @param itemData New item data
   * @returns The created tutorial bank item
   */
  static async createTutorialBankItem(
    itemData: Partial<TutorialBankItem>
  ): Promise<TutorialBankItem> {
    const response = await api.post('/tutorial-bank/items', itemData);
    return response.data.item;
  }

  /**
   * Publish a tutorial bank item
   * @param itemId ID of the item to publish
   * @returns The published tutorial bank item
   */
  static async publishTutorialBankItem(
    itemId: string
  ): Promise<TutorialBankItem> {
    const response = await api.post(`/tutorial-bank/items/${itemId}/publish`);
    return response.data.item;
  }

  /**
   * Delete a tutorial bank item
   * @param itemId ID of the item to delete
   */
  static async deleteTutorialBankItem(itemId: string): Promise<void> {
    await api.delete(`/tutorial-bank/items/${itemId}`);
  }

  /**
   * Generate a thumbnail from a video file
   * @param videoFile Video file to generate thumbnail from
   * @returns Promise with thumbnail as a Blob
   */
  static async generateVideoThumbnail(videoFile: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        // Create a video element to load the video
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        
        // Create object URL for the video file
        const videoUrl = URL.createObjectURL(videoFile);
        video.src = videoUrl;
        
        // Set up event handlers
        video.onloadedmetadata = () => {
          // Seek to 1/3 of the video duration for a representative frame
          video.currentTime = video.duration / 3;
        };
        
        video.onseeked = () => {
          try {
            // Create a canvas to draw the video frame
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              reject(new Error('Failed to get canvas context'));
              return;
            }
            
            // Set canvas dimensions to match video
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            
            // Draw the current video frame to the canvas
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            // Convert canvas to blob
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  resolve(blob);
                } else {
                  reject(new Error('Failed to generate thumbnail'));
                }
                
                // Clean up
                URL.revokeObjectURL(videoUrl);
              },
              'image/jpeg',
              0.8
            );
          } catch (error) {
            reject(error);
          }
        };
        
        video.onerror = () => {
          URL.revokeObjectURL(videoUrl);
          reject(new Error('Error loading video'));
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Upload a tutorial video to Firebase Storage with thumbnail generation
   * @param userId User ID
   * @param file Video file to upload
   * @param onProgress Optional progress callback
   * @returns Promise with object containing video URL and thumbnail URL
   */
  static async uploadTutorialVideo(
    userId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<{ videoUrl: string; thumbnailUrl?: string }> {
    try {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        throw new Error('Only video files are allowed');
      }

      // Convert File to MediaFile format expected by MediaService
      const mediaFile = {
        file,
        preview: URL.createObjectURL(file),
        type: file.type.includes('video') ? MediaType.VIDEO : MediaType.IMAGE,
        progress: 0,
      };

      // Set media upload options
      const options = {
        maxSize: 200 * 1024 * 1024, // 200MB max size for videos
        acceptedTypes: ['video/mp4', 'video/quicktime'],
      };

      // Upload the file using MediaService with progress tracking
      const videoUrl = await MediaService.uploadMedia(
        userId,
        mediaFile,
        options,
        (progress) => {
          onProgress?.(progress.progress);
        }
      );
      
      // Generate and upload thumbnail
      let thumbnailUrl: string | undefined;
      try {
        // Generate thumbnail from the video
        const thumbnailBlob = await this.generateVideoThumbnail(file);
        
        // Create a File object from the Blob
        const thumbnailFile = new File([thumbnailBlob], `${file.name.split('.')[0]}-thumbnail.jpg`, {
          type: 'image/jpeg'
        });
        
        // Upload the thumbnail
        const thumbnailMediaFile = {
          file: thumbnailFile,
          preview: URL.createObjectURL(thumbnailBlob),
          type: MediaType.IMAGE,
          progress: 0,
        };
        
        // Use smaller size limit for thumbnails
        const thumbnailOptions = {
          maxSize: 5 * 1024 * 1024, // 5MB max size for thumbnails
          acceptedTypes: ['image/jpeg', 'image/png'],
        };
        
        thumbnailUrl = await MediaService.uploadMedia(
          userId,
          thumbnailMediaFile,
          thumbnailOptions
        );
        
        console.log('Thumbnail generated and uploaded successfully:', thumbnailUrl);
      } catch (thumbnailError) {
        console.error('Error generating or uploading thumbnail:', thumbnailError);
        // Continue without thumbnail if there's an error
      }

      return { videoUrl, thumbnailUrl };
    } catch (error) {
      console.error('Error uploading tutorial video:', error);
      throw error;
    }
  }
}
